import * as React from "react";

// Thin matchMedia wrapper for inline-style breakpoint decisions. SSR-safe
// (returns the provided default when window isn't available yet) and
// re-renders the component when the query changes — Safari's older
// addListener API is included as a fallback for the few macOS versions
// still in the wild.

export function useMediaQuery(query: string, fallback: boolean = false): boolean {
  const get = React.useCallback(() => {
    if (typeof window === "undefined" || !window.matchMedia) return fallback;
    return window.matchMedia(query).matches;
  }, [query, fallback]);

  const [matches, setMatches] = React.useState<boolean>(get);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent | MediaQueryList) => setMatches("matches" in e ? e.matches : (e as any).matches);
    setMatches(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler as EventListener);
      return () => mql.removeEventListener("change", handler as EventListener);
    }
    // Legacy Safari fallback.
    (mql as any).addListener(handler);
    return () => (mql as any).removeListener(handler);
  }, [query]);

  return matches;
}

// Convenience breakpoints — keeps every screen on the same threshold so a
// future redesign moves all of them together.
export const BREAKPOINTS = {
  mobile:  "(max-width: 767px)",
  tablet:  "(max-width: 1023px)",
  desktop: "(min-width: 1024px)",
} as const;
